'use client';

import { forwardRef, useContext, useId, useRef, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { useIsomorphicLayoutEffect } from '../../hooks/use-isomorphic-layout-effect';
import { useMergeRefs } from '../../hooks/use-merge-refs';
import { DrawerRootContext } from './context/drawer-root.context';
import { drawerBodyStyles } from './styles/drawer-body.styles';
import type { DrawerBodyPropsInterface } from './types/drawer-body.types';

/**
 * @since 10.22.0
 */
export const DrawerBody = forwardRef<HTMLDivElement, DrawerBodyPropsInterface>(
  (props: DrawerBodyPropsInterface, forwardedRef) => {
    const { children, className, css: cssProp, ...rest } = props;
    const { drawer } = useContext(DrawerRootContext);
    const id = useId();
    const styles = css(drawerBodyStyles, cssProp);
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const mergedRefs = useMergeRefs([bodyRef, forwardedRef]);

    /*
     * Chrome and Firefox make overflowing scroll containers keyboard focusable
     * on their own, but Safari never does. Drawer.Content is always
     * height-constrained, so the body is the scrollport: apply `tabIndex` if
     * content overflows at open.
     */
    const [isScrollable, setIsScrollable] = useState(false);

    useIsomorphicLayoutEffect(() => {
      const node = bodyRef.current;
      if (!node) {
        setIsScrollable(false);
        return;
      }

      setIsScrollable(node.scrollHeight > node.clientHeight);
    }, []);

    useIsomorphicLayoutEffect(() => {
      drawer.setDescriptionId(id);
      return () => drawer.setDescriptionId(undefined);
    }, [id, drawer.setDescriptionId]);

    return (
      <div
        ref={mergedRefs}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Drawer.Body"
        id={id}
        // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={isScrollable ? 0 : undefined}
        className={cx('c11n', 'c11n_drawer__body', styles, className)}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

DrawerBody.displayName = 'Drawer.Body';
